/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import {
  accessAPI,
  downloadFile,
  formatFecha,
  formatMonto,
  Titulo,
  Boton,
  Sidebar,
  useIsMobile,
  SelectorArchivo, tituloBase, TablaFiltrable, usePermiso } from "../../shared";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Menu } from "primereact/menu";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { Tooltip } from "primereact/tooltip";
import { Tag } from "primereact/tag";
import { CampoInformacion, CardTotalesPendientes, conIva, useIva } from "../../shared";
import SidebarFactura from "./SidebarFactura";
import useConBiller from "./useConBiller";
import TagExtension from "./TagExtension";
import TagRefacturacion from "./TagRefacturacion";

const FILTROS_FACTURAS = [];
const SEARCH_FIELDS_FACTURAS = [
  "_clienteRazonSocial",
  "_clienteNombreFantasia",
  "numeroFactura",
  "_solicitud"
];
const SEARCH_FIELDS_PLANIFICACIONES = [
  "cliente.razonsocial",
  "cliente.nombrefantasia",
  "propuestaComercial.solicitud.nombrefantasia",
  "solicitudProceso.nombrefantasia"
];

export default function Facturacion() {
  // Con lectura se ve todo; crear, emitir y los menús de acciones piden full
  const { puedeEditar } = usePermiso("admin.facturacion");
  const iva = useIva();
  const [planificaciones, setPlanificaciones] = useState([]);
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emitiendo, setEmitiendo] = useState(false);

  const [facturas, setFacturas] = useState([]);
  const [loadingFacturas, setLoadingFacturas] = useState(true);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [detalleItem, setDetalleItem] = useState(null);
  const [facturaIdDetalle, setFacturaIdDetalle] = useState(null);
  const [facturaEnProceso, setFacturaEnProceso] = useState(null);
  const [planificacionSeleccionada, setPlanificacionSeleccionada] =
    useState(null);

  // Nueva factura directa
  const [sidebarNueva, setSidebarNueva] = useState(false);
  const [loaderNueva, setLoaderNueva] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [nuevaClienteId, setNuevaClienteId] = useState(null);
  const [actividadesDisponibles, setActividadesDisponibles] = useState([]);
  const [descuentosCliente, setDescuentosCliente] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [nuevaItems, setNuevaItems] = useState([]);
  const [nuevaMonedaId, setNuevaMonedaId] = useState(null);
  const [nuevaMasIva, setNuevaMasIva] = useState(false);
  const [nuevaFacturarAId, setNuevaFacturarAId] = useState(null);
  const [nuevoItemTexto, setNuevoItemTexto] = useState("");
  const [nuevaAdenda, setNuevaAdenda] = useState("");
  // Tipo de cambio a la moneda principal. Solo aparece si la moneda de la
  // factura no es la principal; la API lo exige en ese caso.
  const [nuevaTipoCambio, setNuevaTipoCambio] = useState(null);
  const [planTipoCambio, setPlanTipoCambio] = useState(null);

  // Sin Biller el organismo emite el comprobante por fuera: el admin escribe el
  // número y, si ya lo tiene, adjunta el archivo. Si no, lo sube después desde
  // el detalle de la factura.
  const conBiller = useConBiller();
  const [nuevaNumeroFactura, setNuevaNumeroFactura] = useState("");
  const [nuevaArchivo, setNuevaArchivo] = useState(null);
  const [planNumeroFactura, setPlanNumeroFactura] = useState("");
  const [planArchivo, setPlanArchivo] = useState(null);

  // Sube la factura recién emitida. Se hace en un request aparte y no junto con
  // la emisión porque el alta manda los ítems como JSON anidado, y un FormData
  // los aplastaría a texto.
  function subirFacturaEmitida(facturaId, archivo, alTerminar) {
    if (!archivo) return alTerminar();
    accessAPI(
      "POST",
      `admin/facturacion/${facturaId}/comprobante/factura`,
      null,
      () => alTerminar(),
      (respuesta) => {
        alTerminar();
        toast.current?.show({
          severity: "warn",
          summary: "La factura se emitió, pero el archivo no se pudo subir",
          detail:
            (respuesta?.[0]?.msg || "") +
            " Se puede subir después desde el detalle de la factura.",
          sticky: true
        });
      },
      archivo
    );
  }

  // Emitir desde planificaciones (sidebar editable)
  const [sidebarPlanificacion, setSidebarPlanificacion] = useState(false);
  const [planItems, setPlanItems] = useState([]);
  const [planMasIva, setPlanMasIva] = useState(false);
  const [planNuevoItemTexto, setPlanNuevoItemTexto] = useState("");
  const [planActividadesDisponibles, setPlanActividadesDisponibles] = useState(
    []
  );
  const [planFacturarAId, setPlanFacturarAId] = useState(null);
  const [planDescuentosCliente, setPlanDescuentosCliente] = useState([]);
  const [planAdenda, setPlanAdenda] = useState("");

  // Descuentos del cliente del detalle actualmente abierto (planificación o factura)

  const toast = useRef(null);
  const menuFacturaRef = useRef(null);
  const menuPlanificacionRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    document.title = "Facturación";
    cargarPlanificaciones();
    cargarFacturas();
    return () => (document.title = tituloBase());
  }, []);

  function cargarPlanificaciones() {
    accessAPI(
      "GET",
      "admin/facturacion/planificaciones",
      null,
      (respuesta) => {
        setPlanificaciones(respuesta);
        setLoading(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar planificaciones",
          sticky: true
        });
        setLoading(false);
      }
    );
  }

  function cargarFacturas() {
    accessAPI(
      "GET",
      "admin/facturacion/pendientes",
      null,
      (respuesta) => {
        setFacturas(
          respuesta.map((f) => {
            const descripciones = (f.procesos || [])
              .map((p) => p.descripcion)
              .filter(Boolean);
            const solicitudes = (f.propuestas || [])
              .map((p) => p.solicitud?.nombrefantasia)
              .filter(Boolean);
            return {
              ...f,
              _solicitud:
                descripciones.length > 0
                  ? descripciones.join(", ")
                  : solicitudes.length > 0
                    ? solicitudes.join(", ")
                    : "-",
              _clienteRazonSocial: f.cliente?.razonsocial,
              _clienteNombreFantasia: f.cliente?.nombrefantasia
            };
          })
        );
        setLoadingFacturas(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar facturas",
          sticky: true
        });
        setLoadingFacturas(false);
      }
    );
  }

  function eliminarPlanificacion(planificacionId) {
    accessAPI(
      "DELETE",
      `admin/facturacion/planificacion/${planificacionId}`,
      null,
      (respuesta) => {
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta[0]?.msg || "Planificación eliminada"
        });
        cargarPlanificaciones();
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar planificación",
          sticky: true
        });
      }
    );
  }

  const menuPlanificacionItems = planificacionSeleccionada
    ? [
        {
          label: "Eliminar",
          icon: "pi pi-trash",
          command: () => {
            confirmDialog({
              message: "¿Eliminar esta planificación de factura?",
              header: "Confirmar eliminación",
              icon: "pi pi-exclamation-triangle",
              acceptLabel: "Eliminar",
              rejectLabel: "Cancelar",
              acceptClassName: "p-button-danger",
              accept: () => eliminarPlanificacion(planificacionSeleccionada.id)
            });
          }
        }
      ]
    : [];

  function abrirNuevaFactura() {
    setSidebarNueva(true);
    setNuevaClienteId(null);
    setActividadesDisponibles([]);
    setNuevaItems([]);
    setNuevaMonedaId(null);
    setNuevaTipoCambio(null);
    setNuevaMasIva(true);
    setNuevaFacturarAId(null);
    setNuevoItemTexto("");
    setNuevaAdenda("");

    if (clientes.length === 0) {
      accessAPI(
        "GET",
        "admin/cliente",
        null,
        (respuesta) => setClientes(respuesta),
        () => {}
      );
    }
    if (monedas.length === 0) {
      accessAPI(
        "GET",
        "admin/config/moneda?dropdown=true",
        null,
        (respuesta) => setMonedas(respuesta),
        () => {}
      );
    }
  }

  function seleccionarCliente(clienteId) {
    setNuevaClienteId(clienteId);
    setNuevaItems([]);
    setActividadesDisponibles([]);
    setDescuentosCliente([]);
    if (clienteId) {
      const clienteSeleccionado = clientes.find((c) => c.value === clienteId);
      setNuevaFacturarAId(clienteSeleccionado?.facturarAClienteId || null);
      accessAPI(
        "GET",
        `admin/facturacion/cliente/${clienteId}/actividades`,
        null,
        (respuesta) => setActividadesDisponibles(respuesta),
        () => {}
      );
      accessAPI(
        "GET",
        `admin/cliente/${clienteId}/descuentos`,
        null,
        (respuesta) => setDescuentosCliente(respuesta || []),
        () => setDescuentosCliente([])
      );
    } else {
      setNuevaFacturarAId(null);
    }
  }

  function descuentoParaCertificadoTipo(certificadoTipoId) {
    if (!certificadoTipoId) return null;
    return (
      descuentosCliente.find(
        (d) => d.certificadoTipo?.id === certificadoTipoId
      ) || null
    );
  }

  function agregarItem(actividadValue) {
    const actividad = actividadesDisponibles.find(
      (a) => a.value === actividadValue
    );
    if (!actividad || nuevaItems.find((i) => i.actividadid === actividad.value))
      return;
    setNuevaItems((prev) => [
      ...prev,
      {
        _key: `act-${actividad.value}`,
        actividadid: actividad.value,
        descripcion: actividad.etiqueta,
        procesoid: actividad.procesoid,
        certificadoTipoId: actividad.certificadoTipoId || null,
        precio: 0,
        aplicarDescuento: true
      }
    ]);
  }

  function toggleAplicarDescuento(key, aplicar) {
    setNuevaItems((prev) =>
      prev.map((item) =>
        item._key === key ? { ...item, aplicarDescuento: aplicar } : item
      )
    );
  }

  function agregarItemManual() {
    if (!nuevoItemTexto.trim()) return;
    setNuevaItems((prev) => [
      ...prev,
      {
        _key: `manual-${Date.now()}`,
        descripcion: nuevoItemTexto.trim(),
        precio: 0
      }
    ]);
    setNuevoItemTexto("");
  }

  function actualizarPrecioItem(key, precio) {
    setNuevaItems((prev) =>
      prev.map((item) => (item._key === key ? { ...item, precio } : item))
    );
  }

  function eliminarItem(key) {
    setNuevaItems((prev) => prev.filter((item) => item._key !== key));
  }

  function emitirFacturaDirecta() {
    if (nuevaItems.length === 0 || !nuevaMonedaId || !nuevaClienteId) return;
    const sinPrecio = nuevaItems.some((i) => !i.precio || i.precio <= 0);
    if (sinPrecio) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Todos los items deben tener un precio mayor a 0",
        sticky: true
      });
      return;
    }

    setLoaderNueva(true);
    const itemsPayload = nuevaItems.map((i) => {
      const descuento = descuentoParaCertificadoTipo(i.certificadoTipoId);
      return {
        actividadid: i.actividadid,
        descripcion: i.descripcion,
        procesoid: i.procesoid,
        precio: i.precio,
        descuentoId: i.aplicarDescuento && descuento ? descuento.id : null
      };
    });
    accessAPI(
      "POST",
      "admin/facturacion/emitirdirecta",
      {
        clienteId: nuevaClienteId,
        facturarAId: nuevaFacturarAId,
        items: itemsPayload,
        monedaId: nuevaMonedaId,
        tipoCambio: nuevaTipoCambio,
        masIva: nuevaMasIva,
        adenda: nuevaAdenda.trim() || null,
        ...(conBiller ? {} : { numeroFactura: nuevaNumeroFactura.trim() })
      },
      (respuesta) => {
        const terminar = () => {
          setLoaderNueva(false);
          setSidebarNueva(false);
          setNuevaNumeroFactura("");
          setNuevaArchivo(null);
          toast.current?.show({
            severity: "success",
            summary: "Éxito",
            detail: respuesta.msg || "Factura emitida exitosamente"
          });
          cargarFacturas();
        };
        subirFacturaEmitida(respuesta.factura?.id, nuevaArchivo, terminar);
      },
      (respuesta) => {
        setLoaderNueva(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al emitir factura",
          sticky: true
        });
      }
    );
  }

  function emitirFactura() {
    if (seleccionadas.length === 0) return;

    const clienteId = seleccionadas[0].cliente.id;
    const monedaId = seleccionadas[0].moneda.id;
    const mismoClienteYMoneda = seleccionadas.every(
      (p) => p.cliente.id === clienteId && p.moneda.id === monedaId
    );

    if (!mismoClienteYMoneda) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail:
          "Todas las planificaciones seleccionadas deben ser del mismo cliente y moneda",
        sticky: true
      });
      return;
    }

    // Pre-populate items from planificaciones' procesos
    const items = [];
    for (const planificacion of seleccionadas) {
      const procesos = planificacion.procesos || [];
      if (procesos.length > 0) {
        for (const proceso of procesos) {
          items.push({
            _key: `plan-${planificacion.id}-${proceso.descripcion}`,
            descripcion: proceso.descripcion,
            precio: proceso.monto || 0,
            procesoid: proceso.procesoid,
            certificadoTipoId: proceso.certificadoTipoId || null,
            aplicarDescuento: true
          });
        }
      } else {
        items.push({
          _key: `plan-${planificacion.id}`,
          descripcion:
            planificacion.propuestaComercial?.solicitud?.nombrefantasia ||
            planificacion.solicitudProceso?.nombrefantasia ||
            `Planificación #${planificacion.id}`,
          precio: planificacion.monto || 0,
          aplicarDescuento: true
        });
      }
    }

    setPlanItems(items);
    setPlanMasIva(seleccionadas[0].masIva ?? true);
    setPlanTipoCambio(seleccionadas[0].moneda?.tipoCambio ?? null);
    setPlanNuevoItemTexto("");
    setPlanActividadesDisponibles([]);
    setPlanDescuentosCliente(seleccionadas[0].cliente.descuentos || []);
    setPlanFacturarAId(seleccionadas[0].cliente.facturara?.id || null);
    setPlanAdenda("");
    setSidebarPlanificacion(true);

    // Load activities for the client
    accessAPI(
      "GET",
      `admin/facturacion/cliente/${clienteId}/actividades`,
      null,
      (respuesta) => setPlanActividadesDisponibles(respuesta),
      () => {}
    );
    // Descuentos vigentes del cliente (por si cambiaron desde que se cargaron
    // las planificaciones en la grilla).
    accessAPI(
      "GET",
      `admin/cliente/${clienteId}/descuentos`,
      null,
      (respuesta) => setPlanDescuentosCliente(respuesta || []),
      () => {}
    );

    if (clientes.length === 0) {
      accessAPI(
        "GET",
        "admin/cliente",
        null,
        (respuesta) => setClientes(respuesta),
        () => {}
      );
    }
  }

  function confirmarEmitirPlanificacion() {
    if (planItems.length === 0) return;
    const sinPrecio = planItems.some((i) => !i.precio || i.precio <= 0);
    if (sinPrecio) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Todos los items deben tener un precio mayor a 0",
        sticky: true
      });
      return;
    }

    setEmitiendo(true);
    const planItemsPayload = planItems.map((i) => {
      const descuento = descuentoParaCertificadoTipoPlan(i.certificadoTipoId);
      return {
        actividadid: i.actividadid,
        descripcion: i.descripcion,
        procesoid: i.procesoid,
        precio: i.precio,
        descuentoId: i.aplicarDescuento && descuento ? descuento.id : null
      };
    });
    accessAPI(
      "POST",
      "admin/facturacion/emitir",
      {
        planificacionIds: seleccionadas.map((p) => p.id),
        items: planItemsPayload,
        masIva: planMasIva,
        tipoCambio: planTipoCambio,
        facturarAId: planFacturarAId,
        adenda: planAdenda.trim() || null,
        ...(conBiller ? {} : { numeroFactura: planNumeroFactura.trim() })
      },
      (respuesta) => {
        const terminar = () => {
          toast.current?.show({
            severity: "success",
            summary: "Éxito",
            detail: respuesta.msg || "Factura emitida exitosamente"
          });
          setSeleccionadas([]);
          setEmitiendo(false);
          setSidebarPlanificacion(false);
          setPlanNumeroFactura("");
          setPlanArchivo(null);
          cargarPlanificaciones();
          cargarFacturas();
        };
        subirFacturaEmitida(respuesta.factura?.id, planArchivo, terminar);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al emitir factura",
          sticky: true
        });
        setEmitiendo(false);
      }
    );
  }

  function agregarItemPlan(actividadValue) {
    const actividad = planActividadesDisponibles.find(
      (a) => a.value === actividadValue
    );
    if (!actividad || planItems.find((i) => i.actividadid === actividad.value))
      return;
    setPlanItems((prev) => [
      ...prev,
      {
        _key: `act-${actividad.value}`,
        actividadid: actividad.value,
        descripcion: actividad.etiqueta,
        procesoid: actividad.procesoid,
        certificadoTipoId: actividad.certificadoTipoId || null,
        precio: 0,
        aplicarDescuento: true
      }
    ]);
  }

  function descuentoParaCertificadoTipoPlan(certificadoTipoId) {
    if (!certificadoTipoId) return null;
    return (
      planDescuentosCliente.find(
        (d) => d.certificadoTipo?.id === certificadoTipoId
      ) || null
    );
  }

  function togglePlanAplicarDescuento(key, aplicar) {
    setPlanItems((prev) =>
      prev.map((item) =>
        item._key === key ? { ...item, aplicarDescuento: aplicar } : item
      )
    );
  }

  function agregarItemManualPlan() {
    if (!planNuevoItemTexto.trim()) return;
    setPlanItems((prev) => [
      ...prev,
      {
        _key: `manual-${Date.now()}`,
        descripcion: planNuevoItemTexto.trim(),
        precio: 0
      }
    ]);
    setPlanNuevoItemTexto("");
  }

  function marcarPagada(facturaId, archivo) {
    setFacturaEnProceso(facturaId);
    accessAPI(
      "PUT",
      `admin/facturacion/${facturaId}/pagar`,
      null,
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta[0]?.msg || "Factura marcada como pagada"
        });
        cargarFacturas();
      },
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al marcar factura como pagada",
          sticky: true
        });
      },
      archivo
    );
  }

  function descargarRecibo(factura) {
    setFacturaEnProceso(factura.id);
    downloadFile(
      `admin/facturacion/${factura.id}/recibo`,
      `Recibo-${factura.numeroFactura || factura.id}.pdf`,
      () => setFacturaEnProceso(null),
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar recibo",
          sticky: true
        });
      }
    );
  }

  function descargarPDF(factura) {
    setFacturaEnProceso(factura.id);
    downloadFile(
      `admin/facturacion/${factura.id}/pdf`,
      `Factura-${factura.numeroFactura || factura.id}.pdf`,
      () => setFacturaEnProceso(null),
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar factura",
          sticky: true
        });
      }
    );
  }

  function generarNotaCredito(facturaId) {
    setFacturaEnProceso(facturaId);
    accessAPI(
      "POST",
      `admin/facturacion/${facturaId}/notacredito`,
      null,
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta[0]?.msg || "Nota de crédito emitida"
        });
        cargarFacturas();
      },
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al emitir nota de crédito",
          sticky: true
        });
      }
    );
  }

  function descargarNotaCredito(factura) {
    setFacturaEnProceso(factura.id);
    downloadFile(
      `admin/facturacion/${factura.id}/notacredito`,
      `NotaDeCredito-${factura.numeroFactura || factura.id}.pdf`,
      () => setFacturaEnProceso(null),
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar nota de crédito",
          sticky: true
        });
      }
    );
  }

  const menuFacturaItems = facturaSeleccionada
    ? [
        ...(!facturaSeleccionada.fechaPago &&
        !facturaSeleccionada.anulada
          ? [
              {
                label: "Marcar como pagada",
                icon: "pi pi-check",
                command: () => {
                  confirmDialog({
                    message:
                      "¿Marcar esta factura como pagada sin comprobante?",
                    header: "Confirmar",
                    icon: "pi pi-exclamation-triangle",
                    acceptLabel: "Confirmar",
                    rejectLabel: "Cancelar",
                    accept: () => marcarPagada(facturaSeleccionada.id)
                  });
                }
              }
            ]
          : []),
        {
          label: "Descargar factura",
          icon: "pi pi-download",
          command: () => descargarPDF(facturaSeleccionada),
          disabled: !facturaSeleccionada?.idFacturaBiller
        },
        ...(facturaSeleccionada?.idReciboBiller
          ? [
              {
                label: "Descargar comprobante de pago",
                icon: "pi pi-download",
                command: () => descargarRecibo(facturaSeleccionada)
              }
            ]
          : []),
        ...(!facturaSeleccionada.fechaPago &&
        !facturaSeleccionada.anulada &&
        facturaSeleccionada.idFacturaBiller
          ? [
              {
                label: "Generar nota de crédito",
                icon: "pi pi-times-circle",
                command: () => {
                  confirmDialog({
                    message:
                      "¿Emitir una nota de crédito en Biller para anular esta factura por el total? Esta acción no se puede deshacer.",
                    header: "Confirmar nota de crédito",
                    icon: "pi pi-exclamation-triangle",
                    acceptLabel: "Emitir nota de crédito",
                    rejectLabel: "Cancelar",
                    acceptClassName: "p-button-danger",
                    accept: () => generarNotaCredito(facturaSeleccionada.id)
                  });
                }
              }
            ]
          : []),
        ...(facturaSeleccionada?.idNotaDeCreditoBiller ||
        facturaSeleccionada?.archivoNotaCreditoId
          ? [
              {
                label: "Descargar nota de crédito",
                icon: "pi pi-download",
                command: () => descargarNotaCredito(facturaSeleccionada)
              }
            ]
          : [])
      ]
    : [];

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={menuFacturaItems} popup ref={menuFacturaRef} />
      <Menu model={menuPlanificacionItems} popup ref={menuPlanificacionRef} />
      <Sidebar
        visible={!!detalleItem}
        onHide={() => setDetalleItem(null)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        {detalleItem && (
          <div>
            <Titulo>
              {detalleItem.numeroFactura || `Planificación #${detalleItem.id}`}
            </Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Cliente">
                {detalleItem.cliente?.razonsocial || "-"}
              </CampoInformacion>
              {detalleItem.clienteFacturado && (
                <CampoInformacion nombreCampo="Facturado a">
                  {detalleItem.clienteFacturado.razonsocial}
                </CampoInformacion>
              )}
              <CampoInformacion nombreCampo="Monto">
                {detalleItem.moneda?.simbolo} {formatMonto(detalleItem.monto)}
              </CampoInformacion>
              {detalleItem.masIva && (
                <CampoInformacion nombreCampo="Monto con IVA">
                  {detalleItem.moneda?.simbolo}{" "}
                  {formatMonto(conIva(detalleItem.monto, iva))}
                </CampoInformacion>
              )}
              <CampoInformacion nombreCampo="Moneda">
                {detalleItem.moneda?.nombre || "-"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha">
                {formatFecha(detalleItem.fechaCreacion)}
              </CampoInformacion>
              {detalleItem.numeroFactura && (
                <CampoInformacion nombreCampo="Número de factura">
                  {detalleItem.numeroFactura}
                </CampoInformacion>
              )}
            </div>
            {/* Propuestas comerciales */}
            {(() => {
              const propuestas =
                detalleItem.propuestas ||
                (detalleItem.propuestaComercial
                  ? [detalleItem.propuestaComercial]
                  : []);
              if (propuestas.length === 0) return null;
              return (
                <>
                  <Titulo>Propuestas comerciales</Titulo>
                  {propuestas.map((p, index) => (
                    <div
                      key={p.id}
                      css={{
                        padding: "0.5em 0",
                        borderBottom:
                          propuestas.length > 1 && index < propuestas.length - 1
                            ? "1px solid #e0e0e0"
                            : "none",
                        cursor: "pointer",
                        "&:hover": { color: "var(--principal)" }
                      }}
                      onClick={() =>
                        window.open(`/propuesta/${p.id}`, "_blank")
                      }
                    >
                      {p.solicitud?.nombrefantasia || `Propuesta #${p.id}`}
                    </div>
                  ))}
                </>
              );
            })()}
            {/* Procesos / items */}
            {detalleItem.procesos?.length > 0 && (
              <>
                <Titulo>Procesos</Titulo>
                {detalleItem.procesos.map((proceso, index) => {
                  // Descuento:
                  // - Factura emitida: viene persistido en el JSON del proceso
                  //   (descuentoPorcentaje + descuentoConcepto).
                  // - Planificación: lo derivamos de los descuentos del cliente
                  //   cruzando por certificadoTipoId.
                  let descuentoInfo = null;
                  if (proceso.descuentoPorcentaje != null) {
                    descuentoInfo = {
                      porcentajeDescuento: proceso.descuentoPorcentaje,
                      concepto: proceso.descuentoConcepto || ""
                    };
                  } else if (proceso.certificadoTipoId) {
                    const descuentos = detalleItem.cliente?.descuentos || [];
                    descuentoInfo =
                      descuentos.find(
                        (d) =>
                          d.certificadoTipo?.id === proceso.certificadoTipoId
                      ) || null;
                  }
                  return (
                    <div
                      key={index}
                      css={{
                        padding: "0.5em 0",
                        borderBottom:
                          detalleItem.procesos.length > 1 &&
                          index < detalleItem.procesos.length - 1
                            ? "1px solid #e0e0e0"
                            : "none"
                      }}
                    >
                      <div
                        css={{
                          cursor: proceso.procesoid ? "pointer" : "default",
                          "&:hover": proceso.procesoid
                            ? { color: "var(--principal)" }
                            : {}
                        }}
                        onClick={() => {
                          if (proceso.procesoid) {
                            window.open(
                              `/proceso/${proceso.procesoid}`,
                              "_blank"
                            );
                          }
                        }}
                      >
                        {proceso.descripcion}
                      </div>
                      {descuentoInfo && (
                        <div
                          css={{
                            marginTop: "0.35em",
                            fontSize: "0.8em",
                            color: "var(--amarillo)",
                            fontWeight: 600
                          }}
                        >
                          <i
                            className="pi pi-tag"
                            css={{ marginRight: "0.4em" }}
                          />
                          Descuento {descuentoInfo.porcentajeDescuento}%
                          {descuentoInfo.concepto
                            ? ` - ${descuentoInfo.concepto}`
                            : ""}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </Sidebar>
      <Sidebar
        visible={sidebarNueva}
        onHide={() => setSidebarNueva(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nueva factura</Titulo>

        <div css={{ marginBottom: "1em" }}>
          <label
            css={{
              fontWeight: "bold",
              display: "block",
              marginBottom: "0.5em"
            }}
          >
            Cliente
          </label>
          <Dropdown
            value={nuevaClienteId}
            onChange={(e) => seleccionarCliente(e.value)}
            options={clientes}
            optionLabel="etiqueta"
            optionValue="value"
            placeholder="Seleccionar cliente"
            filter
            css={{ width: "100%" }}
          />
        </div>

        {nuevaClienteId && (
          <div css={{ marginBottom: "1em" }}>
            <label
              css={{
                fontWeight: "bold",
                display: "block",
                marginBottom: "0.5em"
              }}
            >
              Facturar a
            </label>
            <Dropdown
              value={nuevaFacturarAId}
              onChange={(e) => setNuevaFacturarAId(e.value)}
              options={clientes}
              optionLabel="etiqueta"
              optionValue="value"
              placeholder="Mismo cliente"
              showClear
              filter
              css={{ width: "100%" }}
            />
          </div>
        )}

        <div css={{ marginBottom: "1em" }}>
          <label
            css={{
              fontWeight: "bold",
              display: "block",
              marginBottom: "0.5em"
            }}
          >
            Moneda
          </label>
          <Dropdown
            value={nuevaMonedaId}
            onChange={(e) => {
              setNuevaMonedaId(e.value);
              // Propone el tipo de cambio de la moneda (el que trae el BCU
              // cada mañana o el último guardado); el admin lo puede cambiar.
              setNuevaTipoCambio(
                monedas.find((m) => m.value === e.value)?.tipoCambio ?? null
              );
            }}
            options={monedas}
            optionLabel="etiqueta"
            optionValue="value"
            placeholder="Seleccionar moneda"
            css={{ width: "100%" }}
          />
        </div>

        {monedas.find((m) => m.value === nuevaMonedaId)?.principal === false && (
          <div css={{ marginBottom: "1em" }}>
            <label
              htmlFor="nuevaTipoCambio"
              css={{
                fontWeight: "bold",
                display: "block",
                marginBottom: "0.5em"
              }}
            >
              Tipo de cambio
            </label>
            <InputNumber
              inputId="nuevaTipoCambio"
              value={nuevaTipoCambio}
              onValueChange={(e) => setNuevaTipoCambio(e.value)}
              mode="decimal"
              minFractionDigits={0}
              maxFractionDigits={6}
              min={0}
              css={{ width: "100%" }}
            />
          </div>
        )}

        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: "0.75em",
            marginBottom: "1.5em"
          }}
        >
          <InputSwitch
            checked={nuevaMasIva}
            onChange={(e) => setNuevaMasIva(e.value)}
          />
          <span>Más IVA</span>
        </div>

        <Divider />

        {nuevaClienteId && (
          <>
            <div css={{ marginBottom: "1em" }}>
              <label
                css={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "0.5em"
                }}
              >
                Agregar actividad
              </label>
              <Dropdown
                value={null}
                onChange={(e) => agregarItem(e.value)}
                options={actividadesDisponibles.filter(
                  (a) => !nuevaItems.find((i) => i.actividadid === a.value)
                )}
                optionLabel="etiqueta"
                optionValue="value"
                placeholder="Seleccionar actividad"
                css={{ width: "100%" }}
                filter
              />
            </div>
            <div css={{ marginBottom: "1em" }}>
              <label
                css={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "0.5em"
                }}
              >
                Agregar ítem manual
              </label>
              <div css={{ display: "flex", gap: "0.5em" }}>
                <InputText
                  value={nuevoItemTexto}
                  onChange={(e) => setNuevoItemTexto(e.target.value)}
                  placeholder="Descripción del ítem"
                  css={{ flex: 1 }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") agregarItemManual();
                  }}
                />
                <Boton
                  callback={agregarItemManual}
                  disabled={!nuevoItemTexto.trim()}
                >
                  <i className="pi pi-plus" />
                </Boton>
              </div>
            </div>
          </>
        )}

        {nuevaItems.length > 0 && (
          <>
            <Divider />
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75em",
                marginBottom: "4em"
              }}
            >
              {nuevaItems.map((item) => {
                const descuento = descuentoParaCertificadoTipo(
                  item.certificadoTipoId
                );
                return (
                  <div
                    key={item._key}
                    css={{
                      padding: "0.75em",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "6px",
                      border: "1px solid #e0e0e0"
                    }}
                  >
                    <div
                      css={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "0.5em"
                      }}
                    >
                      <span css={{ fontSize: "0.9em" }}>
                        {item.descripcion}
                        {item.origen === "extension" && (
                          <TagExtension />
                        )}
                        {item.origen === "refacturacion" && (
                          <TagRefacturacion />
                        )}
                      </span>
                      <Boton
                        texto
                        rojo
                        chico
                        callback={() => eliminarItem(item._key)}
                      >
                        <i className="pi pi-times" />
                      </Boton>
                    </div>
                    <InputNumber
                      value={item.precio}
                      onValueChange={(e) =>
                        actualizarPrecioItem(item._key, e.value || 0)
                      }
                      mode="decimal"
                      minFractionDigits={0}
                      placeholder="Precio"
                      inputStyle={{ width: "100%" }}
                      css={{ width: "100%" }}
                    />
                    {descuento && (
                      <div
                        css={{
                          marginTop: "0.5em",
                          padding: "0.5em 0.75em",
                          backgroundColor: "#fff8e1",
                          border: "1px solid var(--amarillo)",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5em"
                        }}
                      >
                        <Checkbox
                          inputId={`descuento-${item._key}`}
                          checked={!!item.aplicarDescuento}
                          onChange={(e) =>
                            toggleAplicarDescuento(item._key, e.checked)
                          }
                        />
                        <label
                          htmlFor={`descuento-${item._key}`}
                          css={{
                            fontSize: "0.85em",
                            cursor: "pointer",
                            lineHeight: 1.4
                          }}
                        >
                          <strong>
                            Descuento {descuento.porcentajeDescuento}%
                          </strong>
                          {descuento.concepto ? ` - ${descuento.concepto}` : ""}
                          {item.aplicarDescuento && item.precio > 0 && (
                            <div css={{ color: "var(--grisTextos)" }}>
                              Final:{" "}
                              {(
                                item.precio *
                                (1 - descuento.porcentajeDescuento / 100)
                              ).toFixed(2)}
                            </div>
                          )}
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
              <div
                css={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: "bold",
                  padding: "0.5em 0"
                }}
              >
                <span>Total</span>
                <span>
                  {monedas
                    .find((m) => m.value === nuevaMonedaId)
                    ?.etiqueta?.split(" ")[0] || ""}{" "}
                  {nuevaItems
                    .reduce((sum, i) => {
                      const precio = i.precio || 0;
                      const d = i.aplicarDescuento
                        ? descuentoParaCertificadoTipo(i.certificadoTipoId)
                        : null;
                      if (d)
                        return sum + precio * (1 - d.porcentajeDescuento / 100);
                      return sum + precio;
                    }, 0)
                    .toFixed(2)}
                  {nuevaMasIva && " + IVA"}
                </span>
              </div>
            </div>
          </>
        )}

        {nuevaClienteId && conBiller === false && (
          <div css={{ marginBottom: "1.5em" }}>
            <label
              css={{
                fontWeight: "bold",
                display: "block",
                marginBottom: "0.5em"
              }}
            >
              Número de factura
            </label>
            <InputText
              value={nuevaNumeroFactura}
              onChange={(e) => setNuevaNumeroFactura(e.target.value)}
              css={{ width: "100%" }}
            />
            <small css={{ color: "var(--grisTextos)" }}>
              El del comprobante que ya emitió el organismo. Es el que ve el
              cliente.
            </small>

            <label
              css={{
                fontWeight: "bold",
                display: "block",
                margin: "1em 0 0.5em"
              }}
            >
              Archivo de la factura
            </label>
            <SelectorArchivo
              archivo={nuevaArchivo}
              onSeleccion={setNuevaArchivo}
              etiqueta="Elegir factura"
            />
            <small css={{ display: "block", color: "var(--grisTextos)" }}>
              Opcional. Si todavía no lo tenés, se puede subir después desde el
              detalle de la factura.
            </small>
          </div>
        )}

        {nuevaClienteId && (
          <div css={{ marginBottom: "5em" }}>
            <label
              css={{
                fontWeight: "bold",
                display: "block",
                marginBottom: "0.5em"
              }}
            >
              Adenda
            </label>
            <InputTextarea
              value={nuevaAdenda}
              onChange={(e) => setNuevaAdenda(e.target.value)}
              placeholder="Texto adicional para la adenda de la factura"
              rows={3}
              autoResize
              css={{ width: "100%" }}
            />
          </div>
        )}

        <div
          css={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "1em",
            backgroundColor: "white",
            zIndex: 1
          }}
        >
          <Boton
            callback={emitirFacturaDirecta}
            loader={loaderNueva}
            disabled={
              nuevaItems.length === 0 ||
              !nuevaMonedaId ||
              !nuevaClienteId ||
              (conBiller === false && !nuevaNumeroFactura.trim())
            }
            ancho
          >
            Emitir factura
          </Boton>
        </div>
      </Sidebar>

      <Sidebar
        visible={sidebarPlanificacion}
        onHide={() => setSidebarPlanificacion(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        {seleccionadas.length > 0 && (
          <>
            <Titulo>Emitir factura</Titulo>

            <div className="grid">
              <CampoInformacion nombreCampo="Cliente">
                {seleccionadas[0].cliente.razonsocial}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Moneda">
                {seleccionadas[0].moneda.nombre}
              </CampoInformacion>
            </div>

            <div css={{ marginBottom: "1em" }}>
              <label
                css={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "0.5em"
                }}
              >
                Facturar a
              </label>
              <Dropdown
                value={planFacturarAId}
                onChange={(e) => setPlanFacturarAId(e.value)}
                options={clientes}
                optionLabel="etiqueta"
                optionValue="value"
                placeholder="Mismo cliente"
                showClear
                filter
                css={{ width: "100%" }}
              />
            </div>

            <div
              css={{
                display: "flex",
                alignItems: "center",
                gap: "0.75em",
                marginBottom: "1.5em"
              }}
            >
              <InputSwitch
                checked={planMasIva}
                onChange={(e) => setPlanMasIva(e.value)}
              />
              <span>Más IVA</span>
            </div>

            {seleccionadas[0]?.moneda?.principal === false && (
            <div css={{ marginBottom: "1em" }}>
              <label
                htmlFor="planTipoCambio"
                css={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "0.5em"
                }}
              >
                Tipo de cambio
              </label>
              <InputNumber
                inputId="planTipoCambio"
                value={planTipoCambio}
                onValueChange={(e) => setPlanTipoCambio(e.value)}
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={6}
                min={0}
                css={{ width: "100%" }}
              />
            </div>
            )}

            <Divider />

            <div css={{ marginBottom: "1em" }}>
              <label
                css={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "0.5em"
                }}
              >
                Agregar actividad
              </label>
              <Dropdown
                value={null}
                onChange={(e) => agregarItemPlan(e.value)}
                options={planActividadesDisponibles.filter(
                  (a) => !planItems.find((i) => i.actividadid === a.value)
                )}
                optionLabel="etiqueta"
                optionValue="value"
                placeholder="Seleccionar actividad"
                css={{ width: "100%" }}
                filter
              />
            </div>
            <div css={{ marginBottom: "1em" }}>
              <label
                css={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "0.5em"
                }}
              >
                Agregar ítem manual
              </label>
              <div css={{ display: "flex", gap: "0.5em" }}>
                <InputText
                  value={planNuevoItemTexto}
                  onChange={(e) => setPlanNuevoItemTexto(e.target.value)}
                  placeholder="Descripción del ítem"
                  css={{ flex: 1 }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") agregarItemManualPlan();
                  }}
                />
                <Boton
                  callback={agregarItemManualPlan}
                  disabled={!planNuevoItemTexto.trim()}
                >
                  <i className="pi pi-plus" />
                </Boton>
              </div>
            </div>

            {planItems.length > 0 && (
              <>
                <Divider />
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75em",
                    marginBottom: "4em"
                  }}
                >
                  {planItems.map((item) => {
                    const descuento = descuentoParaCertificadoTipoPlan(
                      item.certificadoTipoId
                    );
                    return (
                      <div
                        key={item._key}
                        css={{
                          padding: "0.75em",
                          backgroundColor: "#f8f9fa",
                          borderRadius: "6px",
                          border: "1px solid #e0e0e0"
                        }}
                      >
                        <div
                          css={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: "0.5em"
                          }}
                        >
                          <span css={{ fontSize: "0.9em" }}>
                            {item.descripcion}
                            {item.origen === "extension" && (
                              <TagExtension />
                            )}
                            {item.origen === "refacturacion" && (
                              <TagRefacturacion />
                            )}
                          </span>
                          <Boton
                            texto
                            rojo
                            chico
                            callback={() =>
                              setPlanItems((prev) =>
                                prev.filter((i) => i._key !== item._key)
                              )
                            }
                          >
                            <i className="pi pi-times" />
                          </Boton>
                        </div>
                        <InputNumber
                          value={item.precio}
                          onValueChange={(e) =>
                            setPlanItems((prev) =>
                              prev.map((i) =>
                                i._key === item._key
                                  ? { ...i, precio: e.value || 0 }
                                  : i
                              )
                            )
                          }
                          mode="decimal"
                          minFractionDigits={0}
                          placeholder="Precio"
                          inputStyle={{ width: "100%" }}
                          css={{ width: "100%" }}
                        />
                        {descuento && (
                          <div
                            css={{
                              marginTop: "0.5em",
                              padding: "0.5em 0.75em",
                              backgroundColor: "#fff8e1",
                              border: "1px solid var(--amarillo)",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5em"
                            }}
                          >
                            <Checkbox
                              inputId={`plan-descuento-${item._key}`}
                              checked={!!item.aplicarDescuento}
                              onChange={(e) =>
                                togglePlanAplicarDescuento(item._key, e.checked)
                              }
                            />
                            <label
                              htmlFor={`plan-descuento-${item._key}`}
                              css={{
                                fontSize: "0.85em",
                                cursor: "pointer",
                                lineHeight: 1.4
                              }}
                            >
                              <strong>
                                Descuento {descuento.porcentajeDescuento}%
                              </strong>
                              {descuento.concepto
                                ? ` - ${descuento.concepto}`
                                : ""}
                              {item.aplicarDescuento && item.precio > 0 && (
                                <div css={{ color: "var(--grisTextos)" }}>
                                  Final:{" "}
                                  {(
                                    item.precio *
                                    (1 - descuento.porcentajeDescuento / 100)
                                  ).toFixed(2)}
                                </div>
                              )}
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div
                    css={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontWeight: "bold",
                      padding: "0.5em 0"
                    }}
                  >
                    <span>Total</span>
                    <span>
                      {seleccionadas[0].moneda.simbolo}{" "}
                      {planItems
                        .reduce((sum, i) => {
                          const precio = i.precio || 0;
                          const d = i.aplicarDescuento
                            ? descuentoParaCertificadoTipoPlan(
                                i.certificadoTipoId
                              )
                            : null;
                          if (d)
                            return (
                              sum + precio * (1 - d.porcentajeDescuento / 100)
                            );
                          return sum + precio;
                        }, 0)
                        .toFixed(2)}
                      {planMasIva && " + IVA"}
                    </span>
                  </div>
                </div>
              </>
            )}

            {conBiller === false && (
              <div css={{ marginBottom: "1.5em" }}>
                <label
                  css={{
                    fontWeight: "bold",
                    display: "block",
                    marginBottom: "0.5em"
                  }}
                >
                  Número de factura
                </label>
                <InputText
                  value={planNumeroFactura}
                  onChange={(e) => setPlanNumeroFactura(e.target.value)}
                  css={{ width: "100%" }}
                />
                <small css={{ color: "var(--grisTextos)" }}>
                  El del comprobante que ya emitió el organismo. Es el que ve el
                  cliente.
                </small>

                <label
                  css={{
                    fontWeight: "bold",
                    display: "block",
                    margin: "1em 0 0.5em"
                  }}
                >
                  Archivo de la factura
                </label>
                <SelectorArchivo
                  archivo={planArchivo}
                  onSeleccion={setPlanArchivo}
                  etiqueta="Elegir factura"
                />
                <small css={{ display: "block", color: "var(--grisTextos)" }}>
                  Opcional. Si todavía no lo tenés, se puede subir después desde
                  el detalle de la factura.
                </small>
              </div>
            )}

            <div css={{ marginBottom: "5em" }}>
              <label
                css={{
                  fontWeight: "bold",
                  display: "block",
                  marginBottom: "0.5em"
                }}
              >
                Adenda
              </label>
              <InputTextarea
                value={planAdenda}
                onChange={(e) => setPlanAdenda(e.target.value)}
                placeholder="Texto adicional para la adenda de la factura"
                rows={3}
                autoResize
                css={{ width: "100%" }}
              />
            </div>

            <div
              css={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                padding: "1em",
                backgroundColor: "white",
                zIndex: 1
              }}
            >
              <Boton
                callback={confirmarEmitirPlanificacion}
                loader={emitiendo}
                disabled={
                  planItems.length === 0 ||
                  (conBiller === false && !planNumeroFactura.trim())
                }
                ancho
              >
                Emitir factura
              </Boton>
            </div>
          </>
        )}
      </Sidebar>

      <Titulo
        botones={
          puedeEditar && (
            <>
              {seleccionadas.length > 0 && (
                <Boton callback={emitirFactura} loader={emitiendo}>
                  Emitir factura ({seleccionadas.length})
                </Boton>
              )}
              <Boton transparente callback={abrirNuevaFactura}>
                Nueva factura
              </Boton>
            </>
          )
        }
      >
        Facturación
      </Titulo>

      {loading && (
        <Card>
          <div
            css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <Skeleton width="100%" height="3rem" />
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} width="100%" height="3.5rem" />
            ))}
          </div>
        </Card>
      )}

      {!loading && (
        <Card
          css={{
            // Monto un poco más ancho en pantallas grandes.
            "& .col-monto-plan": {
              width: "12em",
              "@media (max-width: 768px)": { width: "10em" }
            }
          }}
        >
          <Titulo>Planificaciones de factura</Titulo>
          <TablaFiltrable
            value={planificaciones}
            searchFields={SEARCH_FIELDS_PLANIFICACIONES}
            anchoBusqueda="33%"
            nombreArchivo="planificaciones_factura"
            selection={seleccionadas}
            onSelectionChange={(e) => setSeleccionadas(e.value)}
            selectionMode="checkbox"
            dataKey="id"
            rows={10}
            onRowClick={(e) => {
              const target = e.originalEvent.target;
              if (
                target.closest(".p-selection-column") ||
                target.closest(".p-checkbox") ||
                target.closest("button")
              )
                return;
              setDetalleItem(e.data);
            }}
            rowClassName={() => "cursor-pointer"}
            emptyMessage="No hay planificaciones de factura"
          >
            <Column selectionMode="multiple" headerStyle={{ width: "3em" }} />
            <Column
              header="Cliente"
              body={(row) => {
                const c = row.cliente;
                const nombre =
                  c.nombrefantasia && c.nombrefantasia !== c.razonsocial
                    ? `${c.razonsocial} (${c.nombrefantasia})`
                    : c.razonsocial;
                if (c.facturara) {
                  return `${nombre} (factura a: ${c.facturara.razonsocial})`;
                }
                return nombre;
              }}
            />
            <Column
              header="Solicitud"
              body={(row) =>
                row.propuestaComercial?.solicitud?.nombrefantasia ||
                row.solicitudProceso?.nombrefantasia ||
                "-"
              }
            />
            <Column
              header="Monto"
              body={(row) => {
                const simbolo = row.moneda?.simbolo || "";
                const base = `${simbolo} ${formatMonto(row.monto)}`;
                if (!row.masIva) return base;
                const total = formatMonto(conIva(row.monto, iva));
                return (
                  <>
                    <Tooltip target={`.monto-iva-plan-${row.id}`} />
                    <span
                      className={`monto-iva-plan-${row.id}`}
                      data-pr-tooltip={`${simbolo} ${total}`}
                      data-pr-position="top"
                    >
                      {base} + IVA
                    </span>
                  </>
                );
              }}
              exportValue={(row) =>
                `${row.moneda?.simbolo || ""} ${formatMonto(row.monto)}${
                  row.masIva ? " + IVA" : ""
                }`
              }
              className="col-monto-plan"
              headerClassName="col-monto-plan"
            />
            <Column
              header="Fecha"
              body={(row) => formatFecha(row.fechaCreacion)}
              style={{ width: "10em" }}
            />
            <Column
              body={(row) =>
                puedeEditar && (
                  <Boton
                    texto
                    negro
                    callback={(e) => {
                      setPlanificacionSeleccionada(row);
                      menuPlanificacionRef.current.toggle(e);
                    }}
                  >
                    <i className="pi pi-ellipsis-v" />
                  </Boton>
                )
              }
              style={{ width: "60px", textAlign: "center" }}
            />
          </TablaFiltrable>
        </Card>
      )}

      {!loadingFacturas && (
        <div
          css={{
            marginTop: "1em",
            // CardTotalesPendientes trae margen inferior para cuando se usa
            // sola; acá el espacio lo pone la card de abajo
            "& > .p-card": { marginBottom: 0 }
          }}
        >
          <CardTotalesPendientes facturas={facturas} />
        </div>
      )}

      {!loadingFacturas && (
        <Card css={{ marginTop: "1em" }}>
          <Titulo>Facturas pendientes de pago</Titulo>
          <TablaFiltrable
            key={`facturas-${facturaEnProceso}`}
            value={facturas}
            filtros={FILTROS_FACTURAS}
            searchFields={SEARCH_FIELDS_FACTURAS}
            nombreArchivo="facturas_pendientes"
            dataKey="id"
            stripedRows
            rows={10}
            onRowClick={(e) => {
              const target = e.originalEvent.target;
              if (target.closest("button") || target.closest(".p-menu")) return;
              setFacturaIdDetalle(e.data.id);
            }}
            rowClassName={() => "cursor-pointer"}
            emptyMessage="No hay facturas pendientes de pago"
          >
            <Column
              header="Número"
              field="numeroFactura"
              style={{ width: "10em" }}
            />
            {!isMobile && (
              <Column
                header="Cliente"
                body={(row) => {
                  const c = row.cliente;
                  const nombre =
                    c.nombrefantasia && c.nombrefantasia !== c.razonsocial
                      ? `${c.razonsocial} (${c.nombrefantasia})`
                      : c.razonsocial;
                  if (row.clienteFacturado) {
                    return `${nombre} (factura a: ${row.clienteFacturado.razonsocial})`;
                  }
                  return nombre;
                }}
              />
            )}
            {!isMobile && (
              <Column
                header="Descripción"
                body={(row) => {
                  const items = (row.procesos || [])
                    .map((p) => p.descripcion)
                    .filter(Boolean);
                  const lista =
                    items.length > 0
                      ? items
                      : (row.propuestas || [])
                          .map((p) => p.solicitud?.nombrefantasia)
                          .filter(Boolean);
                  if (lista.length === 0) return "-";
                  const visibles = lista.slice(0, 2);
                  const hayMas = lista.length > 2;
                  return visibles.join(", ") + (hayMas ? "…" : "");
                }}
              />
            )}
            {!isMobile && (
              <Column
                header="Monto"
                body={(row) => {
                  const simbolo = row.moneda?.simbolo || "";
                  const base = `${simbolo} ${formatMonto(row.monto)}`;
                  if (!row.masIva) return base;
                  const total = formatMonto(conIva(row.monto, iva));
                  return (
                    <>
                      <Tooltip target={`.monto-iva-fact-${row.id}`} />
                      <span
                        className={`monto-iva-fact-${row.id}`}
                        data-pr-tooltip={`${simbolo} ${total}`}
                        data-pr-position="top"
                      >
                        {base} + IVA
                      </span>
                    </>
                  );
                }}
                style={{ width: "10em" }}
              />
            )}
            {!isMobile && (
              <Column
                header="Fecha"
                body={(row) => formatFecha(row.fechaCreacion)}
                style={{ width: "10em" }}
              />
            )}
            {isMobile && (
              <Column
                header="Estado"
                body={(row) =>
                  row.anulada ? (
                    <Tag value="Anulada" severity="danger" />
                  ) : row.fechaPago ? (
                    <Tag value="Pagada" severity="success" />
                  ) : (
                    <Tag value="Pendiente" severity="warning" />
                  )
                }
                style={{ textAlign: "center" }}
              />
            )}
            <Column
              body={(row) => (
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5em"
                  }}
                >
                  {facturaEnProceso === row.id && (
                    <i className="pi pi-spin pi-spinner" />
                  )}
                  {puedeEditar && (
                    <Boton
                      texto
                      negro
                      callback={(e) => {
                        setFacturaSeleccionada(row);
                        menuFacturaRef.current.toggle(e);
                      }}
                    >
                      <i className="pi pi-ellipsis-v" />
                    </Boton>
                  )}
                </div>
              )}
              style={{ width: "80px", textAlign: "center" }}
            />
          </TablaFiltrable>
        </Card>
      )}
      <SidebarFactura
        facturaId={facturaIdDetalle}
        onHide={() => setFacturaIdDetalle(null)}
        onAccion={() => {
          cargarFacturas();
          cargarPlanificaciones();
        }}
      />
    </>
  );
}
